'use client';
import * as React from 'react';
import invariant from 'invariant';
import PropTypes from 'prop-types';
import { match } from 'path-to-regexp';

import { RouterContext } from '../shared/context';
import { type PageContainerProps } from '../PageContainer';

import { Show } from './Show';
import { Edit } from './Edit';
import { Create } from './Create';
import { DataSourceCache } from './cache';
import { CrudProvider } from './CrudProvider';
import { List, ListSlotProps, type ListSlots } from './List';
import { CrudFormSlots, CrudFormSlotProps } from './CrudForm';
import { OmitId, DataModel, DataSource, DataModelId } from './types';

export interface CrudProps<D extends DataModel> {
  /**
   * Root path to CRUD pages.
   */
  rootPath: string;
  /**
   * Initial number of rows to show per page.
   * @default 100
   */
  initialPageSize?: number;
  /**
   * Server-side [data source](https://mui.com/toolpad/core/react-crud/#data-sources).
   */
  dataSource: DataSource<D>;
  /**
   * Default form values for a new item.
   * @default {}
   */
  defaultValues?: Partial<OmitId<D>>;
  /**
   * [Cache](https://mui.com/toolpad/core/react-crud/#data-caching) for the data source.
   */
  dataSourceCache?: DataSourceCache | null;
  /**
   * The title of each CRUD page.
   */
  pageTitles?: {
    list?: string;
    show?: string;
    edit?: string;
    create?: string;
  };
  /**
   * The props used for each slot inside.
   * @default {}
   */
  slotProps?: {
    list?: ListSlotProps;
    form?: CrudFormSlotProps;
    pageContainer?: PageContainerProps;
  };
  /**
   * The components used for each slot inside.
   * @default {}
   */
  slots?: {
    list?: ListSlots;
    form?: CrudFormSlots;
    pageContainer?: React.JSXElementConstructor<PageContainerProps>;
  };
}
/**
 *
 * Demos:
 *
 * - [CRUD](https://mui.com/toolpad/core/react-crud/)
 *
 * API:
 *
 * - [Crud API](https://mui.com/toolpad/core/api/crud)
 */
function Crud<D extends DataModel>(props: CrudProps<D>) {
  const {
    slots,
    rootPath,
    slotProps,
    dataSource,
    pageTitles,
    defaultValues,
    initialPageSize,
    dataSourceCache,
  } = props;

  const listPath = rootPath;
  const showPath = `${rootPath}/:id`;
  const createPath = `${rootPath}/new`;
  const editPath = `${rootPath}/:id/edit`;

  const routerContext = React.useContext(RouterContext);

  const handleRowClick = React.useCallback(
    (id: string | number) => {
      routerContext?.navigate(`${rootPath}/${String(id)}`);
    },
    [rootPath, routerContext]
  );

  const handleCreateClick = React.useCallback(() => {
    routerContext?.navigate(createPath);
  }, [createPath, routerContext]);

  const handleEditClick = React.useCallback(
    (id: string | number) => {
      routerContext?.navigate(`${rootPath}/${String(id)}/edit`);
    },
    [rootPath, routerContext]
  );

  const handleCreate = React.useCallback(() => {
    routerContext?.navigate(listPath);
  }, [listPath, routerContext]);

  const handleEdit = React.useCallback(() => {
    routerContext?.navigate(listPath);
  }, [listPath, routerContext]);

  const handleDelete = React.useCallback(() => {
    routerContext?.navigate(listPath);
  }, [listPath, routerContext]);

  const renderedRoute = React.useMemo(() => {
    const pathname = routerContext?.pathname ?? '';

    if (match(listPath)(pathname)) {
      return (
        <List<D>
          onRowClick={handleRowClick}
          pageTitle={pageTitles?.list}
          onEditClick={handleEditClick}
          initialPageSize={initialPageSize}
          onCreateClick={handleCreateClick}
          slots={{
            ...(slots?.pageContainer
              ? {
                  pageContainer: slots?.pageContainer,
                }
              : {}),
            ...slots?.list,
          }}
          slotProps={{
            ...(slotProps?.pageContainer
              ? {
                  pageContainer: slotProps?.pageContainer,
                }
              : {}),
            ...slotProps?.list,
          }}
        />
      );
    }
    if (match(createPath)(pathname)) {
      return (
        <Create<D>
          resetOnSubmit={false}
          initialValues={defaultValues}
          onSubmitSuccess={handleCreate}
          pageTitle={pageTitles?.create}
          slots={{
            ...(slots?.form
              ? {
                  form: slots?.form,
                }
              : {}),
            ...(slots?.pageContainer
              ? {
                  pageContainer: slots?.pageContainer,
                }
              : {}),
          }}
          slotProps={{
            ...(slotProps?.form
              ? {
                  form: slotProps?.form,
                }
              : {}),
            ...(slotProps?.pageContainer
              ? {
                  pageContainer: slotProps?.pageContainer,
                }
              : {}),
          }}
        />
      );
    }
    const showMatch = match<{ id: DataModelId }>(showPath)(pathname);
    if (showMatch) {
      const resourceId = showMatch.params.id;
      invariant(resourceId, 'No resource ID present in URL.');
      return (
        <Show<D>
          id={resourceId}
          onDelete={handleDelete}
          pageTitle={pageTitles?.show}
          onEditClick={handleEditClick}
          slots={{
            ...(slots?.pageContainer
              ? {
                  pageContainer: slots?.pageContainer,
                }
              : {}),
          }}
          slotProps={{
            ...(slotProps?.pageContainer
              ? {
                  pageContainer: slotProps?.pageContainer,
                }
              : {}),
          }}
        />
      );
    }
    const editMatch = match<{ id: DataModelId }>(editPath)(pathname);
    if (editMatch) {
      const resourceId = editMatch.params.id;
      invariant(resourceId, 'No resource ID present in URL.');
      return (
        <Edit<D>
          id={resourceId}
          onSubmitSuccess={handleEdit}
          pageTitle={pageTitles?.edit}
          slots={{
            ...(slots?.form
              ? {
                  form: slots?.form,
                }
              : {}),
            ...(slots?.pageContainer
              ? {
                  pageContainer: slots?.pageContainer,
                }
              : {}),
          }}
          slotProps={{
            ...(slotProps?.form
              ? {
                  form: slotProps?.form,
                }
              : {}),
            ...(slotProps?.pageContainer
              ? {
                  pageContainer: slotProps?.pageContainer,
                }
              : {}),
          }}
        />
      );
    }
    return null;
  }, [
    createPath,
    defaultValues,
    editPath,
    handleCreate,
    handleCreateClick,
    handleDelete,
    handleEdit,
    handleEditClick,
    handleRowClick,
    initialPageSize,
    listPath,
    pageTitles,
    routerContext?.pathname,
    showPath,
    slotProps,
    slots,
  ]);

  return (
    <CrudProvider<D> dataSource={dataSource} dataSourceCache={dataSourceCache}>
      {renderedRoute}
    </CrudProvider>
  );
}

Crud.propTypes /* remove-proptypes */ = {
  /**
   * Default form values for a new item.
   * @default {}
   */
  defaultValues: PropTypes.object,
  /**
   * Initial number of rows to show per page.
   * @default 100
   */
  initialPageSize: PropTypes.number,
  /**
   * Root path to CRUD pages.
   */
  rootPath: PropTypes.string.isRequired,
  // ┌────────────────────────────── Warning ──────────────────────────────┐
  // │ These PropTypes are generated from the TypeScript type definitions. │
  // │ To update them, edit the TypeScript types and run `pnpm proptypes`. │
  // └─────────────────────────────────────────────────────────────────────┘
  /**
   * Server-side [data source](https://mui.com/toolpad/core/react-crud/#data-sources).
   */
  dataSource: PropTypes.object.isRequired,
  /**
   * The title of each CRUD page.
   */
  pageTitles: PropTypes.shape({
    edit: PropTypes.string,
    list: PropTypes.string,
    show: PropTypes.string,
    create: PropTypes.string,
  }),
  /**
   * [Cache](https://mui.com/toolpad/core/react-crud/#data-caching) for the data source.
   */
  dataSourceCache: PropTypes.shape({
    get: PropTypes.func.isRequired,
    set: PropTypes.func.isRequired,
    clear: PropTypes.func.isRequired,
    ttl: PropTypes.number.isRequired,
    cache: PropTypes.object.isRequired,
  }),
  /**
   * The props used for each slot inside.
   * @default {}
   */
  slotProps: PropTypes.shape({
    pageContainer: PropTypes.object,
    list: PropTypes.shape({
      dataGrid: PropTypes.object,
      pageContainer: PropTypes.object,
    }),
    form: PropTypes.shape({
      select: PropTypes.object,
      checkbox: PropTypes.object,
      textField: PropTypes.object,
      datePicker: PropTypes.object,
      dateTimePicker: PropTypes.object,
    }),
  }),
  /**
   * The components used for each slot inside.
   * @default {}
   */
  slots: PropTypes.shape({
    pageContainer: PropTypes.elementType,
    list: PropTypes.shape({
      dataGrid: PropTypes.func,
      pageContainer: PropTypes.elementType,
    }),
    form: PropTypes.shape({
      select: PropTypes.elementType,
      checkbox: PropTypes.elementType,
      textField: PropTypes.elementType,
      datePicker: PropTypes.elementType,
      dateTimePicker: PropTypes.elementType,
    }),
  }),
} as any;

export { Crud };
